import {
	submitEvent,
	submitMerge,
	submitMiniAction,
	submitMove,
	trackOnNextPageImpression
} from "@otto-ec/tracking-bct";

const DEFAULT_FEATURE_NUMBER = 99;

export class TrackHelper {
	sendTileViewTrackingEvent(viewTrackingInfo, position, comboType, isACFA = false) {
		const widgetPayload = this._createWidgetPayloadWithNumberOfCombos(
			viewTrackingInfo.tiles.length,
			comboType,
			"loaded",
			position,
			isACFA
		);

		const combosPayload = this._generatePageImpressionCombosPayload(viewTrackingInfo, isACFA);

		const eventPayload = [widgetPayload].concat(combosPayload);
		submitMerge({}, eventPayload);
	}

	sendTileClickTrackingEvent(clickTrackingInfo, position, comboType, isACFA = false) {
		const widgetPayload = this._createWidgetPayloadWithNumberOfCombos(
			clickTrackingInfo.numberOfCombos,
			comboType,
			"clicked",
			position,
			isACFA
		);

		const hasSimilarityScore = clickTrackingInfo.clickedCombo.similarityScore !== null;

		const clickedComboPayload = this._generateClickedComboPayload(clickTrackingInfo, hasSimilarityScore, isACFA);

		const eventPayload = {
			name: "click",
			features: [widgetPayload].concat(clickedComboPayload)
		};
		const featureName = isACFA ? "WozdaCombos" : "SimilarCombos";
		const oldTracking = {
			promo_ComboType: comboType,
			"wk.promo_AttributionSource": clickTrackingInfo.clickedCombo.comboSource,
			"wk.promo_AttributionFeature": this.buildAttributionFeature(featureName, comboType)
		};
		submitMove(oldTracking, eventPayload);
		trackOnNextPageImpression({ promo_Click: featureName });
	}

	buildAttributionFeature(featureName, comboType) {
		const pageCluster = window.o_util.misc.getPagecluster();
		const comboTypeCase = comboType[0].toUpperCase() + comboType.substring(1).toLowerCase();
		return `${featureName}${comboTypeCase}_${pageCluster}`;
	}

	trackViewForCombo(combo, articlesToTrack, articlesCountToTrack, featureName = "SingleCombo") {
		let viewTrackingInfo;
		if (combo.getPresentation() === "LAYER") {
			viewTrackingInfo = {
				promo_WozdaLayer: this._generateLegacyTrackingInfo(combo, "view"),
				ot_VariationIds: this._generateVariationTrackingInfo(combo, "promo_WozdaLayer"),
				ot_PageCluster: "Artikelkombiseite",
				promo_ComboType: combo.getComboType()
			};
		} else {
			viewTrackingInfo = {
				ot_PageCluster: "Artikelkombiseite",
				promo_ComboType: combo.getComboType()
			};
		}
		submitMerge(viewTrackingInfo);
		const trackingInfo = this._generateTrackingPayloadForCombo(
			combo,
			featureName,
			"loaded",
			articlesToTrack,
			articlesCountToTrack
		);

		submitMerge({}, trackingInfo);
	}

	clickHandlerForArticleClickTracking(index, combo, featureName = "SingleCombo") {
		const clickedArticle = combo.getArticles()[index % combo.getArticleCount()];
		return () => {
			const trackingMoveInfo = this._getTrackingClickTrackingInfoForMove(combo, clickedArticle, featureName);
			const newMoveInfo = this._getNewClickTrackingInfoForMove(combo, clickedArticle, featureName);
			if (newMoveInfo && trackingMoveInfo) {
				submitMove(trackingMoveInfo, newMoveInfo);
				trackOnNextPageImpression({ promo_Click: featureName });
			}
		};
	}

	trackComboVisibility(combo, articlesToTrack, articlesCountToTrack, featureName) {
		const featureSequence = this._getFeatureSequence(combo.getDom());
		const parentId = `combo-${featureName}-${featureSequence}`;

		const payload = [
			{
				featureId: parentId
			}
		];

		const articleTracking = articlesToTrack.slice(0, articlesCountToTrack).map(article => {
			const articlePosition = article.position;
			return {
				featureId: `${parentId}-Article-${articlePosition}`
			};
		});

		const trackingInfo = payload.concat(articleTracking);
		submitMiniAction(trackingInfo);
	}

	sendScrollRightOrLeftTrackingEvent(comboCinema, featureName, visibleArticlesPosition, action) {
		const visibleArticles = comboCinema
			.getArticles()
			.filter(article => visibleArticlesPosition.includes(article.position - 1));

		const trackingInfo = this._generateTrackingPayloadForCombo(
			comboCinema,
			featureName,
			"visible",
			visibleArticles,
			visibleArticles.length
		);
		const trackingPayload = {
			name: action,
			features: trackingInfo
		};
		submitEvent({}, trackingPayload);
	}

	_generateTrackingPayloadForCombo(combo, featureName, status, articlesToTrack, articlesCountToTrack) {
		const featureSequence = this._getFeatureSequence(combo.getDom());
		const parentId = `combo-${featureName}-${featureSequence}`;
		const position = this._getFeatureNumber(combo.getDom());
		const articleCount = Number.parseInt(combo.getArticleCount());
		const comboId = combo.getId();
		const comboSource = combo.getSource();
		const comboType = combo.getComboType();
		const comboTracking = [
			{
				id: parentId,
				name: featureName,
				position: featureSequence,
				status: status,
				labels: {
					promo_FeatureNumber: [`${position}`],
					promo_FilledSlots: [`${articleCount}`],
					promo_Feature: ["true"],
					promo_Content: [comboId],
					promo_Source: [comboSource],
					promo_ComboType: [comboType]
				}
			}
		];

		const articleTracking = articlesToTrack.slice(0, articlesCountToTrack).map(article => {
			return this._generateTrackingPayloadForArticle(
				article,
				combo.getArticleNumberFromADS(),
				parentId,
				featureName,
				status
			);
		});

		return comboTracking.concat(articleTracking);
	}

	_generateTrackingPayloadForArticle(article, articleNumberFromADS, parentId, featureName, status) {
		const articlePosition = article.position;
		const comingFromThisArticlesADS = article.id === articleNumberFromADS;
		return {
			id: `${parentId}-Article-${articlePosition}`,
			parentId: parentId,
			name: `${featureName}-Article`,
			position: articlePosition,
			variationId: article.variationId,
			status: status,
			labels: {
				order_ProductOnWishlist: [`${article.onWishlist}`],
				promo_ComingFromThisArticle: [`${comingFromThisArticlesADS}`]
			}
		};
	}

	_generateLegacyTrackingInfo(combo, status) {
		return (
			"" +
			combo.getId() +
			"∞" +
			this._getFeatureSequence(combo.getDom()) +
			"$" +
			this._getFeatureNumber(combo.getDom()) +
			"∞" +
			combo.getArticleCount() +
			"∞" +
			"n/a∞" +
			combo.getSource() +
			"∞" +
			status
		);
	}

	_getFeatureNumber(container) {
		if (container.dataset.feature_number != null) {
			return Number.parseInt(container.dataset.feature_number, 0);
		} else if (document.getElementsByTagName("html")[0].dataset.pagecluster === "Artikelkombiseite") {
			return 1;
		} else {
			return DEFAULT_FEATURE_NUMBER;
		}
	}

	_getFeatureSequence(container) {
		if (container.parentElement.dataset.featureOrder != null) {
			return Number.parseInt(container.parentElement.dataset.featureOrder, 0);
		} else if (document.getElementsByTagName("html")[0].dataset.pagecluster === "Artikelkombiseite") {
			return 1;
		} else {
			return DEFAULT_FEATURE_NUMBER;
		}
	}

	_generateVariationTrackingInfo(combo, feature) {
		const trackingInfo = {};
		Array.prototype.map.call(combo.getArticles(), article => {
			const variationId = article.getVariationId();
			trackingInfo[variationId] = {
				product_ListPosition: [article.getPosition().toString()],
				product_ListFeature: [feature]
			};

			if (combo.isComingFromArticleDetailPage()) {
				trackingInfo[variationId]["promo_ComingFromThisArticle"] =
					article.id === combo.getArticleNumberFromADS();
			}

			trackingInfo[variationId]["order_ProductOnWishlist"] = "" + article.onWishlist;
		});
		return trackingInfo;
	}

	_getTrackingClickTrackingInfoForMove(combo, clickedArticle, featureName) {
		const productOnWhishlist = clickedArticle.onWishlist;
		const source = combo.source;

		const legacyTrackingInfo = {
			promo_ComboType: combo.getComboType(),
			"wk.promo_AttributionFeature": this.buildAttributionFeature(featureName, combo.getComboType()),
			"wk.promo_AttributionSource": source
		};

		if (combo.getTopicId()) {
			legacyTrackingInfo["wk.sd_TopicId"] = combo.getTopicId();
		}

		if (clickedArticle) {
			legacyTrackingInfo["order_ProductOnWishlist"] = productOnWhishlist;
		}

		return legacyTrackingInfo;
	}

	_getNewClickTrackingInfoForMove(combo, clickedArticle, featureName) {
		const featureSequence = this._getFeatureSequence(combo.getDom());
		const parentId = `combo-${featureName}-` + featureSequence;
		const position = this._getFeatureNumber(combo.getDom());
		const articleCount = Number.parseInt(combo.getArticleCount());
		const comboId = combo.getId();
		const comboSource = combo.getSource();
		const articlePosition = clickedArticle.getPosition();
		const variationId = clickedArticle.getVariationId();
		const productOnWhishlist = clickedArticle.onWishlist;
		const comingFromThisArticle = clickedArticle.id === combo.getArticleNumberFromADS();
		const comboType = combo.getComboType();

		return {
			name: "click",
			features: [
				{
					id: parentId,
					name: featureName,
					position: featureSequence,
					status: "clicked",
					labels: {
						promo_FeatureNumber: [`${position}`],
						promo_FilledSlots: [`${articleCount}`],
						promo_Feature: ["true"],
						promo_Content: [comboId],
						promo_Source: [comboSource],
						promo_ComboType: [comboType]
					}
				},
				{
					id: parentId + "-Article-" + articlePosition,
					parentId: parentId,
					name: `${featureName}-Article`,
					position: articlePosition,
					variationId: variationId,
					status: "clicked",
					labels: {
						order_ProductOnWishlist: [`${productOnWhishlist}`],
						...(combo.isComingFromArticleDetailPage() && {
							promo_ComingFromThisArticle: [`${comingFromThisArticle}`]
						})
					}
				}
			]
		};
	}

	_createWidgetPayloadWithNumberOfCombos(numberOfCombos, comboType, status, position, isACFA) {
		return {
			id: isACFA ? "combo-WozdaCombos" : "combo-SimCombos",
			name: isACFA ? "WozdaCombos" : "SimilarCombos",
			position: position,
			status: status,
			labels: {
				promo_FilledSlots: [`${numberOfCombos}`],
				promo_ComboType: [comboType],
				promo_FeatureNumber: ["1"],
				promo_Feature: ["true"]
			}
		};
	}

	_generateClickedComboPayload(clickTrackingInfo, hasSimilarityScore, isACFA) {
		return {
			id: (isACFA ? "combo-WozdaCombo-" : "combo-SimCombo-") + clickTrackingInfo.clickedCombo.position,
			parentId: isACFA ? "combo-WozdaCombos" : "combo-SimCombos",
			name: isACFA ? "WozdaCombo" : "SimilarCombo",
			position: clickTrackingInfo.clickedCombo.position,
			status: "clicked",
			labels: {
				promo_Content: [clickTrackingInfo.clickedCombo.id],
				promo_Source: [clickTrackingInfo.clickedCombo.comboSource],
				...(!isACFA &&
					hasSimilarityScore && {
						promo_ComboSimilarity: [`${clickTrackingInfo.clickedCombo.similarityScore}`]
					})
			}
		};
	}

	_generatePageImpressionCombosPayload(viewTrackingInfo, isACFA) {
		return viewTrackingInfo.tiles.map((comboInfo, index) => {
			const hasSimilarityScore = comboInfo.similarityScore !== null;
			return {
				id: (isACFA ? "combo-WozdaCombo-" : "combo-SimCombo-") + (index + 1),
				parentId: isACFA ? "combo-WozdaCombos" : "combo-SimCombos",
				name: isACFA ? "WozdaCombo" : "SimilarCombo",
				position: index + 1,
				status: "loaded",
				labels: {
					promo_Content: [comboInfo.id],
					...(!isACFA && hasSimilarityScore && { promo_ComboSimilarity: [`${comboInfo.similarityScore}`] }),
					promo_Source: [comboInfo.comboSource]
				}
			};
		});
	}
}
